import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ScreenContext } from './_screen.context';
import { useScreenContext } from './use_screen.context';

interface Props {
	children: ReactNode;
	target?: string;
}

const Container = styled.div`
	display: contents;

	&.is-hidden {
		display: none;
	}
`;

export function ScreenProvider({ target, children }: Props): JSX.Element {
	const [active, setActive] = useState<string | null>(null);
	const screensRef = useRef(new Set<string>());
	const protectedScreensRef = useRef(new Set<string>());
	const [screenStack, setScreenStack] = useState<string[]>([]);

	useEffect(() => {
		if (active === null) return;

		setScreenStack((prev) => {
			const stack = [...prev];
			stack.push(active);

			return stack;
		});
	}, [active]);

	return (
		<ScreenContext.Provider
			value={{
				target,
				active,
				screenStack,
				protectedScreens: protectedScreensRef.current,
				screens: screensRef.current,
				activate: (link) => {
					setActive(link);
				},
				dismiss: () => {
					setActive(null);
				},
			}}
		>
			<ScreenOrigin>{children}</ScreenOrigin>
		</ScreenContext.Provider>
	);
}

interface ScreenOriginProps {
	children: ReactNode;
}

function ScreenOrigin({ children }: ScreenOriginProps): JSX.Element {
	const { active, protectedScreens, target } = useScreenContext();
	const canHideMain = active != null && !protectedScreens.has(active);

	useEffect(() => {
		if (target == null) return;

		const element = document.getElementById(target) as HTMLDivElement;
		if (canHideMain) {
			element.classList.add('is-hidden');
			element.tabIndex = -1;
		} else {
			element.classList.remove('is-hidden');
			// Reason: Angular hacks
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			element.tabIndex = undefined;
		}

		return () => {
			const element = document.getElementById(target) as HTMLDivElement;
			element.classList.remove('is-hidden');
			// Reason: Angular hacks
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			element.tabIndex = undefined;
		};
	}, [canHideMain, target]);

	return (
		<Container className={canHideMain ? 'is-hidden' : undefined} tabIndex={canHideMain ? -1 : undefined}>
			{children}
		</Container>
	);
}
